import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Footprints, Lock, Loader2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Layout, Loading, usePageTitle } from "@/components/site/Layout";
import { GuestSelect } from "@/components/site/StayPicker";
import { PriceBreakdown } from "@/components/site/PriceBreakdown";
import { GuestForm } from "@/components/site/GuestForm";
import { useAvailability, useStayPicker } from "@/hooks/useAvailability";
import { useSite } from "@/context/SiteContext";
import { api, errMsg, fromISO, money, toISO } from "@/lib/api";

const PausedNotice = ({ message }) => (
  <div className="mt-10 max-w-2xl border border-border bg-white rounded-lg p-8 leading-relaxed" data-testid="bookings-paused-message">{message}</div>
);

const useQuote = (range, guests) => {
  const [state, setState] = useState({});
  useEffect(() => {
    if (!range?.from || !range?.to) { setState({}); return; }
    setState({ loading: true });
    api.post("/quote", { check_in: toISO(range.from), check_out: toISO(range.to), guests })
      .then((r) => setState({ quote: r.data }))
      .catch((e) => setState({ error: errMsg(e) }));
  }, [range, guests]);
  return state;
};

export default function Book() {
  const { site } = useSite();
  if (!site) return <Layout><Loading /></Layout>;
  return <BookInner site={site} />;
}

function BookInner({ site }) {
  const [params] = useSearchParams();
  const picker = useStayPicker(site.pricing.min_nights, { from: fromISO(params.get("ci")), to: fromISO(params.get("co")) });
  const { range, setRange, onSelect, disabled, loading, hint, picking } = picker;
  const [guests, setGuests] = useState(Math.min(2, Math.max(1, Number(params.get("g")) || 1)));
  const { quote, error, loading: qLoading } = useQuote(range, guests);
  const [months, setMonths] = useState(window.innerWidth >= 1024 ? 2 : 1);
  usePageTitle("Book Your Stay | Garage Getaway Beaumont, Beaumont TX");
  useEffect(() => {
    api.post("/track", { type: "booking_page_view" }).catch(() => {});
    const onR = () => setMonths(window.innerWidth >= 1024 ? 2 : 1);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);
  const p = site.pricing;
  return (
    <Layout>
      <section className="px-5 sm:px-8 lg:px-12 py-12 sm:py-16 max-w-7xl mx-auto" data-testid="book-page">
        <p className="eyebrow">Book Your Stay</p>
        <h1 className="mt-3 text-4xl sm:text-5xl font-medium">{site.bookings_active ? "Choose your dates" : "Reservations are paused"}</h1>
        {!site.bookings_active ? <PausedNotice message={site.pause_message} /> : (<>
        <p className="mt-3 text-sm" data-testid="book-rules"><strong data-testid="book-nightly-rate">{money(p.nightly_rate)} per night</strong> <span className="text-muted-foreground">· Minimum stay: {p.min_nights} nights · Up to {p.max_guests} guests{p.checkin_time && ` · Check-in from ${p.checkin_time}`}{p.checkout_time && ` · Check-out by ${p.checkout_time}`}</span></p>
        <div className="mt-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7 space-y-6">
            <div className="border border-border rounded-lg bg-white p-2 sm:p-4 relative" data-testid="booking-calendar">
              {loading && <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10"><Loader2 className="animate-spin" /></div>}
              <Calendar mode="range" selected={range} onSelect={onSelect} disabled={disabled} numberOfMonths={months} defaultMonth={range?.from} className="mx-auto w-fit" />
              <div className="flex flex-wrap justify-between items-center px-3 pb-2 text-xs text-muted-foreground gap-2">
                <span data-testid="calendar-hint" className={picking ? "text-foreground font-semibold" : ""}>{hint} Crossed-out dates are unavailable.</span>
                <button onClick={() => setRange(undefined)} className="underline" data-testid="clear-dates-button">Clear dates</button>
              </div>
            </div>
            <div className="max-w-[220px]"><GuestSelect guests={guests} setGuests={setGuests} testid="book-guest-select" /></div>
            <div className="flex gap-3 p-4 border border-[hsl(30_50%_75%)] bg-[hsl(35_70%_94%)] rounded-lg text-sm" data-testid="book-stairs-notice">
              <Footprints className="w-5 h-5 shrink-0 text-[hsl(30_50%_35%)]" />
              <p><strong>Stair access required.</strong> The studio is reached by stairs and is not step-free or wheelchair accessible.</p>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="border border-border rounded-lg bg-white p-6 lg:sticky lg:top-24">
              <p className="font-serif text-2xl">Your stay</p>
              {!quote && !error && !qLoading && <p className="mt-3 text-sm text-muted-foreground" data-testid="select-dates-hint">Select a check-in date, then a check-out date at least {p.min_nights} nights later, to see the full price.</p>}
              {qLoading && <p className="mt-3 text-sm text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking availability…</p>}
              {error && <p className="mt-3 text-sm text-destructive" role="alert" data-testid="quote-error">{error}</p>}
              {quote && <div className="mt-3"><PriceBreakdown quote={quote} /></div>}
              {quote && <GuestForm quote={quote} />}
              <p className="mt-5 text-xs text-muted-foreground flex items-center gap-1.5"><Lock className="w-3 h-3" /> Payment is processed securely by Stripe. Card details never touch this site.</p>
              <p className="mt-2 text-xs text-muted-foreground">{site.policies.payment_terms} See the <Link to="/policies/cancellation" className="underline">cancellation policy</Link>.</p>
            </div>
          </aside>
        </div>
        </>)}
      </section>
    </Layout>
  );
}
